import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DonationCampaign, SupporterTier } from '@rehome/shared';
import { formatHUF, useCampaigns, useShelters, getImageUrl, getAmount, createDonation, createSubscription, createVolunteerApplication } from '@rehome/shared';
import { Heart, Target, Gift, Coffee, Award, Crown, CheckCircle2, X, Loader2, Building2, HandHeart } from 'lucide-react';

// Supporter tiers as specified in plan.md 4.2.6 (Bronz 2000 / Ezüst 5000 / Arany Gazda 10000).
// `dbTier` must match the supporter_subscriptions.tier CHECK constraint:
// tier IN ('Bronz','Ezüst','Arany Gazda').
const SUPPORTER_TIERS: Array<SupporterTier & { dbTier: string }> = [
  { id: 'bronze', name: 'Bronz Támogató', dbTier: 'Bronz', monthlyAmount: 2000, benefits: ['Hírlevél', 'Virtuális simogatás'], color: 'bronze' },
  { id: 'silver', name: 'Ezüst Támogató', dbTier: 'Ezüst', monthlyAmount: 5000, benefits: ['Exkluzív videók', 'Név a weboldalon', 'Hírlevél'], color: 'silver' },
  { id: 'gold', name: 'Arany Gazda', dbTier: 'Arany Gazda', monthlyAmount: 10000, benefits: ['Személyes látogatás', 'Ajándékcsomag', 'Kiemelt státusz'], color: 'gold' },
];

/** Marker prefix for direct-to-shelter donations (no real campaign row exists). */
const SHELTER_CAMPAIGN_PREFIX = 'shelter-';

// ─── DonationPortalPage ────────────────────────────────────────────────────

const DonationPortalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'subscribe' | 'shelters'>('campaigns');
  const { data: campaignsData, loading } = useCampaigns();
  const { data: sheltersData } = useShelters();
  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentCampaign, setPaymentCampaign] = useState<DonationCampaign | null>(null);
  const [donationAmount, setDonationAmount] = useState(5000);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [volunteerOpen, setVolunteerOpen] = useState(false);
  const [volunteerShelterId, setVolunteerShelterId] = useState<string | null>(null);
  const [volunteerForm, setVolunteerForm] = useState({ name: '', email: '', phone: '', type: 'Szakmunka' });
  const [volunteerSuccess, setVolunteerSuccess] = useState(false);
  const [volunteerSubmitting, setVolunteerSubmitting] = useState(false);

  // Sync campaigns from hook, allowing local updates for instant UI feedback
  React.useEffect(() => {
    if (campaignsData) setCampaigns(campaignsData);
  }, [campaignsData]);

  // Deep link from the landing page: /donate?campaign=<id> opens the payment modal
  const requestedCampaignId = searchParams.get('campaign');
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  React.useEffect(() => {
    if (deepLinkHandled || !requestedCampaignId || !campaignsData?.length) return;
    const target = campaignsData.find(c => c.id === requestedCampaignId);
    if (target) {
      setPaymentCampaign(target);
      setPaymentOpen(true);
      setDeepLinkHandled(true);
    }
  }, [requestedCampaignId, campaignsData, deepLinkHandled]);

  // Use local state for rendering so progress updates instantly
  const displayCampaigns = campaigns.length > 0 ? campaigns : (campaignsData ?? []);

  const getProgress = (raised: number, target: number) => {
    if (!target || !raised && raised !== 0) return 0;
    return Math.min(100, Math.round((raised / target) * 100));
  };

  const openPayment = (campaign: DonationCampaign) => {
    setPaymentCampaign(campaign); setPaymentOpen(true); setShowSuccess(false); setDonationAmount(5000); setCustomAmount(''); setDonorName('');
  };

  const handleDonate = useCallback(async () => {
    if (!paymentCampaign) return;
    setProcessing(true);

    const amount = customAmount ? Number(customAmount) : donationAmount;
    if (!amount || amount <= 0) {
      setProcessing(false);
      return;
    }

    // Direct shelter donations use a synthetic "shelter-<uuid>" id that is
    // NOT a campaign row — inserting it as campaign_id would fail (not a UUID
    // / FK violation). Send campaignId: null and keep only the shelter.
    const isShelterDonation = paymentCampaign.id.startsWith(SHELTER_CAMPAIGN_PREFIX);

    const result = await createDonation({
      campaignId: isShelterDonation ? null : paymentCampaign.id,
      amount,
      shelterId: paymentCampaign.shelterId || null,
      donorName: donorName || null,
      message: `Támogatás: ${paymentCampaign.title}`,
    });

    setProcessing(false);

    if (result) {
      setShowSuccess(true);
      // Update local campaign progress: X + Y, not just Y
      setCampaigns(prev => prev.map(c => {
        if (c.id !== paymentCampaign.id) return c;
        const currentRaised = getAmount(c, 'raisedAmount', 'raised_amount');
        return { ...c, raisedAmount: currentRaised + amount };
      }));
    }
  }, [paymentCampaign, donationAmount, customAmount, donorName]);

  const openVolunteerModal = (shelterId: string | null) => {
    setVolunteerShelterId(shelterId);
    setVolunteerSuccess(false);
    setVolunteerOpen(true);
  };

  const handleVolunteerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setVolunteerSubmitting(true);
    const ok = await createVolunteerApplication({
      name: volunteerForm.name,
      email: volunteerForm.email,
      phone: volunteerForm.phone || null,
      helpType: volunteerForm.type,
      shelterId: volunteerShelterId,
    });
    setVolunteerSubmitting(false);
    if (ok) {
      setVolunteerSuccess(true);
      setTimeout(() => {
        setVolunteerOpen(false);
        setVolunteerForm({ name: '', email: '', phone: '', type: 'Szakmunka' });
      }, 2500);
    }
  };

  const handleSubscribe = async (tier: SupporterTier & { dbTier: string }) => {
    setProcessing(true);
    // supporter_subscriptions.tier CHECK only allows 'Bronz'/'Ezüst'/'Arany Gazda'
    await createSubscription({ tier: tier.dbTier, monthlyAmount: tier.monthlyAmount });
    setProcessing(false);
  };

  const RECOMMENDED = [1000, 3000, 5000, 10000];
  const colorMap: Record<string, string> = { bronze: 'border-orange-200 bg-orange-50', silver: 'border-gray-300 bg-gray-50', gold: 'border-yellow-300 bg-yellow-50' };
  const btnMap: Record<string, string> = { bronze: 'bg-orange-500 hover:bg-orange-600', silver: 'bg-gray-600 hover:bg-gray-700', gold: 'bg-yellow-500 hover:bg-yellow-600' };
  const iconMap: Record<string, React.FC<{ className?: string }>> = { bronze: Coffee, silver: Award, gold: Crown };

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      <div className="text-center space-y-4 pt-8">
        <h1 className="text-4xl font-bold text-gray-900">Támogasd a <span className="text-teal-600">ReHome</span> lakóit</h1>
        <p className="text-gray-600 max-w-2xl mx-auto text-lg">Minden adomány közvetlenül az állatok ellátására, orvosi költségeire és rehabilitációjára fordítódik.</p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center gap-4 flex-wrap">
        <button onClick={() => setActiveTab('campaigns')} className={`px-8 py-3 rounded-full font-bold transition-all flex items-center gap-2 ${activeTab === 'campaigns' ? 'bg-teal-600 text-white shadow-lg scale-105' : 'bg-white text-gray-600 hover:bg-gray-50 border'}`}>
          <Target className="w-5 h-5" /> Aktív Kampányok
        </button>
        <button onClick={() => setActiveTab('shelters')} className={`px-8 py-3 rounded-full font-bold transition-all flex items-center gap-2 ${activeTab === 'shelters' ? 'bg-teal-600 text-white shadow-lg scale-105' : 'bg-white text-gray-600 hover:bg-gray-50 border'}`}>
          <Building2 className="w-5 h-5" /> Menhelyek
        </button>
        <button onClick={() => setActiveTab('subscribe')} className={`px-8 py-3 rounded-full font-bold transition-all flex items-center gap-2 ${activeTab === 'subscribe' ? 'bg-rose-500 text-white shadow-lg scale-105' : 'bg-white text-gray-600 hover:bg-gray-50 border'}`}>
          <Heart className="w-5 h-5" /> Havi Támogató
        </button>
      </div>

      {/* CAMPAIGNS TAB */}
      {activeTab === 'campaigns' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-full text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto mb-4" /><p className="text-gray-500">Kampányok betöltése...</p></div>
          ) : displayCampaigns.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-400"><Heart className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>Jelenleg nincs aktív kampány.</p></div>
          ) : displayCampaigns.map(campaign => {
            const raised = getAmount(campaign, 'raisedAmount', 'raised_amount');
            const target = getAmount(campaign, 'targetAmount', 'target_amount');
            const pct = getProgress(raised, target);
            return (
              <div key={campaign.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
                <div className="relative h-48 bg-gray-200">
                  {getImageUrl(campaign) ? (
                    <img src={getImageUrl(campaign)!} alt={campaign.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">Nincs kép</div>
                  )}
                  <div className="absolute top-4 right-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full font-bold backdrop-blur-sm">
                    {campaign.animalId ? 'Orvosi Gyűjtés' : 'Általános'}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{campaign.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">{campaign.description}</p>
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between text-sm font-bold">
                      <span className="text-teal-600">{formatHUF(raised)}</span>
                      <span className="text-gray-400">Cél: {formatHUF(target)}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div className="bg-gradient-to-r from-teal-500 to-emerald-400 h-3 rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-right text-xs text-gray-500">{pct}% teljesítve</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openPayment(campaign)} className="flex-1 bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors shadow-md">Támogatom</button>
                    {campaign.acceptsVolunteers && (
                      <button onClick={() => openVolunteerModal(campaign.shelterId || null)}
                        title="Önkéntes jelentkezés"
                        className="px-4 py-3 rounded-xl font-bold border-2 border-teal-200 text-teal-700 hover:bg-teal-50 transition-colors flex items-center gap-1.5">
                        <HandHeart className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SHELTERS TAB — all shelters as donation recipients */}
      {activeTab === 'shelters' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {!sheltersData || sheltersData.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-400"><Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" /><p>Jelenleg nincs elérhető menhely.</p></div>
          ) : sheltersData.map(shelter => (
            <div key={shelter.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
              <div className="relative h-40 bg-gradient-to-br from-teal-600 to-emerald-500 flex items-center justify-center">
                <Building2 className="w-16 h-16 text-white/30" />
                {shelter.isVerified && (
                  <div className="absolute top-4 right-4 bg-white/90 text-teal-700 text-xs px-3 py-1 rounded-full font-bold">
                    ✓ Hitelesített
                  </div>
                )}
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{shelter.name}</h3>
                <p className="text-gray-500 text-sm mb-3">{shelter.location}</p>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">{shelter.description}</p>
                <div className="text-sm text-gray-500 mb-4 space-y-1">
                  <div>Kapacitás: <span className="font-medium">{shelter.capacity} állat</span></div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setPaymentCampaign({
                        id: `${SHELTER_CAMPAIGN_PREFIX}${shelter.id}`,
                        title: `${shelter.name} támogatása`,
                        description: `Általános támogatás a(z) ${shelter.name} részére`,
                        targetAmount: 1000000, raisedAmount: 0,
                        shelterId: shelter.id,
                        active: true, isActive: true,
                      } as DonationCampaign);
                      setPaymentOpen(true); setShowSuccess(false); setDonationAmount(5000); setCustomAmount(''); setDonorName('');
                    }}
                    className="flex-1 bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors shadow-md text-center"
                  >
                    Támogatom
                  </button>
                  <button onClick={() => openVolunteerModal(shelter.id)}
                    title="Önkéntes jelentkezés"
                    className="px-4 py-3 rounded-xl font-bold border-2 border-teal-200 text-teal-700 hover:bg-teal-50 transition-colors flex items-center">
                    <HandHeart className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SUBSCRIPTION TAB */}
      {activeTab === 'subscribe' && (
        <div className="space-y-8">
          <div className="bg-rose-50 border border-rose-100 p-6 rounded-2xl text-center max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-rose-800 mb-2">Miért legyél rendszeres támogató?</h3>
            <p className="text-rose-700">A kiszámítható havi bevételek teszik lehetővé a legsúlyosabb esetek ellátását is. Csatlakozz a Virtuális Gazdik közösségéhez!</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {SUPPORTER_TIERS.map(tier => {
              const Icon = iconMap[tier.id] || Gift;
              return (
                <div key={tier.id} className={`rounded-2xl border-2 p-8 flex flex-col h-full hover:scale-105 transition-transform ${colorMap[tier.color] || 'border-gray-200'}`}>
                  <div className="flex flex-col items-center flex-1">
                    <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mb-4"><Icon className="w-8 h-8 text-gray-700" /></div>
                    <h3 className="text-2xl font-bold text-gray-900 text-center">{tier.name}</h3>
                    <div className="text-3xl font-bold text-gray-800 my-4 text-center">{formatHUF(tier.monthlyAmount)} <span className="text-sm font-normal text-gray-500">/hó</span></div>
                    <ul className="space-y-3 w-full flex-1">
                      {tier.benefits.map((b, i) => (
                        <li key={i} className="flex items-center text-sm text-gray-700"><CheckCircle2 className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />{b}</li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => handleSubscribe(tier)}
                    disabled={processing}
                    className={`w-full py-3 text-white font-bold rounded-xl shadow-md transition-colors mt-6 disabled:opacity-50 flex items-center justify-center gap-2 ${btnMap[tier.color] || 'bg-teal-600'}`}>
                    {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Kiválasztás
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Volunteer Modal (plan.md 4.2.6: név, email, telefon, segítség típusa) */}
      {volunteerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setVolunteerOpen(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2"><HandHeart className="w-5 h-5 text-teal-600" /> Önkéntes jelentkezés</h3>
              <button onClick={() => setVolunteerOpen(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {volunteerSuccess ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-xl font-bold">Köszönjük!</h4>
                <p className="text-gray-500">A jelentkezésedet rögzítettük, hamarosan keresni fognak.</p>
              </div>
            ) : (
              <form onSubmit={handleVolunteerSubmit} className="space-y-3">
                <input type="text" required placeholder="Neved *" value={volunteerForm.name}
                  onChange={e => setVolunteerForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" />
                <input type="email" required placeholder="Email címed *" value={volunteerForm.email}
                  onChange={e => setVolunteerForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" />
                <input type="tel" placeholder="Telefonszámod" value={volunteerForm.phone}
                  onChange={e => setVolunteerForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" />
                <select value={volunteerForm.type}
                  onChange={e => setVolunteerForm(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white">
                  <option value="Szakmunka">Szakmunka (építés, javítás)</option>
                  <option value="Kutyasétáltatás">Kutyasétáltatás</option>
                  <option value="Állatgondozás">Állatgondozás</option>
                  <option value="Szállítás">Szállítás</option>
                  <option value="Adminisztráció">Adminisztráció</option>
                  <option value="Egyéb">Egyéb</option>
                </select>
                <button type="submit" disabled={volunteerSubmitting}
                  className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                  {volunteerSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandHeart className="w-4 h-4" />}
                  Jelentkezem önkéntesnek
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentOpen && paymentCampaign && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPaymentOpen(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">{paymentCampaign.title}</h3>
              <button onClick={() => setPaymentOpen(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {showSuccess ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-xl font-bold">Köszönjük!</h4>
                <p className="text-gray-500">Az adományodat sikeresen rögzítettük.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  {RECOMMENDED.map(amt => (
                    <button key={amt} onClick={() => setDonationAmount(amt)} className={`px-4 py-2 rounded-lg font-bold border-2 transition-all ${donationAmount === amt ? 'border-teal-500 bg-teal-50' : 'border-gray-200'}`}>{formatHUF(amt)}</button>
                  ))}
                </div>
                <input type="number" placeholder="Egyedi összeg" value={customAmount} onChange={e => setCustomAmount(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 mb-3" />
                <input type="text" placeholder="Neved (opcionális)" value={donorName} onChange={e => setDonorName(e.target.value)} className="w-full border border-gray-300 rounded-lg p-2 mb-4 text-sm" />
                <button onClick={handleDonate} disabled={processing} className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold disabled:opacity-50">
                  {processing ? 'Feldolgozás...' : `${formatHUF(customAmount ? Number(customAmount) : donationAmount)} — Fizetés`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationPortalPage;
